import { colors, fontFamily, fontSize, fontWeight } from '@ardoq/design-tokens';
import { createGlobalStyle, css } from 'styled-components';
import { AG_GRID_SELECTION_COLUMN_ID } from './columns/selectAndDeleteColumnDefinition';
import { deleteComponentColumnId } from './createComponents/getDeleteColumnDefinition';

const cellTypography = css`
  font-family: ${fontFamily.regular};
  font-weight: ${fontWeight.regular};
  font-size: ${fontSize.regular};
  line-height: 1.5;
`;

export const InventoryStyles = createGlobalStyle`
  .ardoq-inventory {
    --ag-header-background-color: ${colors.surfaceDefault};

    /* Default cell styles for all AG Grid cells */
    .ag-cell {
      ${cellTypography}
      display: flex;
      align-items: center;
    }

    /* Popup cell editors (reference, date, date-range) render inside .ag-popup,
       outside .ag-cell, so they can't inherit the cell typography above. */
    .ag-popup-editor {
      ${cellTypography}
    }

    /* Apply Ardoq font family to AG Grid column headers */
    .ag-header-cell-text {
      font-family: ${fontFamily.regular};
    }

    /* A column outside any field group spans both header rows, which centres
       its label across the group row. Confining the wrapper to the bottom row
       lands the label where a grouped column's label sits. */
    .ag-header-cell.ag-header-span-height .ag-header-cell-comp-wrapper {
      height: var(--ag-header-height);
      align-self: flex-end;
    }

    /* While editing, the multiselect/tags input can be taller than the row, so
       overflow: visible shows all of it. "safe center" vertically centers the
       input when it fits, but falls back to top-alignment once it grows taller
       than the cell, so a multi-value input grows downward instead of upward
       under the column header. */
    .ag-cell.cell-select-multiple-list.ag-cell-inline-editing {
      overflow: visible;
      align-items: safe center;
    }

    /**
    * Remove extra padding from for the select checkbox in the ag-grid,
    so that both the checkbox and the delete button are centered vertically.
    */
    [role='gridcell'][col-id='${AG_GRID_SELECTION_COLUMN_ID}'],[role='gridcell'][col-id='${deleteComponentColumnId}'] {
      display: flex;
      align-items: center;
      justify-content: center;
      padding-left: 0;
      padding-right: 0;
    }

    [role='gridcell'][col-id='${AG_GRID_SELECTION_COLUMN_ID}']
      .ag-cell-wrapper
      .ag-selection-checkbox {
      display: none; /* This hides the default AG-grid checkbox */
    }

    [role='gridcell'][col-id='${AG_GRID_SELECTION_COLUMN_ID}']
      .ag-cell-wrapper
      .ardoq-icon {
      margin: auto; /* This horizontally centers the checkbox for the selection column */
    }

    /* Hide the fill handle for readonly cells */
    [role='gridcell'].cell-readonly .ag-fill-handle {
        display: none !important;
    }

    [role='gridcell'].cell-readonly {
        cursor: not-allowed;
     }

    .ag-cell.ag-cell-not-inline-editing.ag-cell-auto-height.cell-editable,
    .ag-cell.ag-cell-not-inline-editing.ag-cell-auto-height.cell-readonly {
        overflow: hidden; /* autoHeight columns spill out of the cell otherwise */
    }

    /* Increase the size of the AG Grid column chooser dialog */
    [role='dialog'][aria-label='Choose Columns'] {
        min-width: 400px !important;
        min-height: 500px !important;
    }

    .row-with-error {
        background-color: ${colors.surfaceError};
    }

    .cell-with-error {
        box-shadow:
             1px 0 0 ${colors.borderError},
             0 1px 0 ${colors.borderError},
             -1px 0 0 ${colors.borderError},
             0 -1px 0 ${colors.borderError};
    }
    
    [role='gridcell'][col-id='name'] .ag-cell-wrapper {
        flex: 1; /* stretches name column to take full width */
    }

    /* Fix vertical alignment for tree data group cells */
    .ag-cell-wrapper.ag-row-group {
        height: 100%;
    }

    .ag-group-value {
        height: 100%;
        display: flex;
        align-items: center;
    }

    /* In hierarchy mode, stretch the inner renderer to fill the row so the
       validation error icon lands on the right side of the cell */
    [role='gridcell'][col-id='name'].cell-with-error .ag-group-value {
        flex: 1;
    }

    .ag-group-child-count {
      font-size: ${fontSize.regular};
    }

    /* Fix indentation for drag handle when hierarchy is not enabled */
    .ag-drag-handle.ag-row-drag.ag-invisible {
        width: 0 !important;
        margin: 0;
    }

    /* Add padding around the drag handle icon */
    .ag-drag-handle.ag-row-drag:not(.ag-invisible) {
        padding-left: 14px;
    }

    /* Fix expandable name column alignment */
    .ag-cell-wrapper.ag-cell-expandable.ag-row-group  {
        display: flex !important;
        align-items: center !important;
    }

    /* Ignore space for chevrons if no grouping, also for new components (indent-undefined) */
    [role='gridcell'][col-id='name'].skip-hierarchy-space .ag-cell-wrapper.ag-row-group-leaf-indent.ag-row-group-indent-0,
    [role='gridcell'][col-id='name'].skip-hierarchy-space .ag-cell-wrapper.ag-row-group-leaf-indent.ag-row-group-indent-undefined {
        margin-left: 0 !important;
    }

    /* Prevent name column from overflowing */
    [role='gridcell'][col-id='name'] {
      overflow: hidden;
    }

    /* Hide the active filter indicator icon for locked columns */
    .ag-header-cell.locked-filter-column .ag-filter-icon {
      display: none;
    }

    /* Visual indication for row drag target */
    .ag-row.ag-row-drag-over {
      background-color: ${colors.surfaceActive} !important;
      border-top: 1px solid ${colors.borderActionActive};
      border-bottom: 1px solid ${colors.borderActionActive};
    }

    .ag-row.ag-row-drag-over .ag-cell {
      background-color: transparent;
    }

    .ag-tooltip {
      max-width: 280px;
      overflow-wrap: anywhere;
    }
  }
`;
